import appConfig from '../../config/appConfig';
import ACTIVATION_ERRORS_DATA from '../../consts/analytics/activationErrorsData';
import {NOT_FOUND} from '../../consts/app/httpErrorsTypes';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as findDeviceActionTypes from '../../consts/uam/findDeviceActionTypes';
import * as uamMethods from '../../consts/uam/uamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {setFetchTokenInProgress} from '../../state/ducks/auth';
import {
    setDataCollectionLastRequestId,
    setYapActivationInProgress,
    setYapAssets,
    setYapCancelDataCollectionInProgress,
    setYapLastRequestId,
    setYapRequestActionTypeData,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {selectIsDiagnosticFlowDataCollectionInProgress} from '../../state/selectors/advancedDiagnostic';
import {selectIsHolderConnected} from '../../state/selectors/iotDevice';
import {
    makeSelectAsset,
    makeSelectAssetIdByType,
    makeSelectDataCollectionLastRequestId,
    makeSelectIsYapActivationInProgress,
    makeSelectIsYapDataCollectionAllowed,
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectIsYapUsageFlowDataCollectionInProgress,
    makeSelectYapCancelDataCollectionInProgress,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import {amplitudeADTracking, amplitudeDataCollectionTracking} from '../amplitude';
import analyticsService from '../analyticsService';
import appErrorService from '../app/appErrorService';
import cmService from '../communicationLayer/cmService';
import iccService from '../icc/iccService';
import log from '../logger/log';
import {uamAssetsMapping} from '../mapping/uamMappings';
import userBrowserService from '../user/userBrowserService';
import advanceDiagnosticService from '../yap/advanceDiagnosticService';
import yapClientService from '../yap/yapClientService';
import yapConnectionService from '../yap/yapConnectionService';
import yapFlowTimeoutService from '../yap/yapFlowTimeoutService';
import yapStateService from '../yap/yapStateService';
import * as dataCollectionService from './dataCollectionService';
import UamClient from './uamClient';

const getAssets = async (isHolder) => {
    const response = await new UamClient().callGet({
        methodName: uamMethods.GET_ASSETS,
        isHolder,
        ignoreErrorCodes: NOT_FOUND,
        isNeedRedirectForOtherErrors: false,
    });

    const [deviceAssets] = response || [];

    if (!deviceAssets) {
        log.error(`uamClientService: failed to get assets. isHolder: ${isHolder}`, {
            logMessageType: logMessageTypes.UAM,
        });

        return false;
    }

    const assets = uamAssetsMapping(deviceAssets, isHolder);

    dispatch(setYapAssets(assets));
    yapStateService.updateAssetMedia(assets);
    yapStateService.checkIsDeviceActivated();

    return true;
};

const getUamTokenAndAssets = async (isHolder) => {
    const isValid = await iccService.fetchUamToken({isHolder});

    dispatch(setFetchTokenInProgress(false));

    if (!isValid) return false;

    getAssets(isHolder); // request to get assets is asynchronous

    return true;
};

const getUamInfo = async (assetId) => {
    if (!assetId) return false;

    const deviceInfos = await new UamClient().callGet({
        methodName: uamMethods.GET_DEVICE_INFO,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

    if (!deviceInfos) return false;

    const deviceInfo = deviceInfos ? deviceInfos[0] : {};

    if (!deviceInfo) return false;

    synchronizeDataRequestCurrentTries = 0;
    return yapStateService.updateIotDevice(assetId, deviceInfo);
};

const updateUamSettings = async (body, isHolder) => {
    const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(getState());

    if (isYapUpdateSettingsInProgress) return;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.UPDATE_SETTINGS, isHolder);

    dispatch(setYapUpdateSettingsInProgress(true, isHolder));
    dispatch(setYapRequestActionTypeData({isHolder}));

    const logBody = yapClientService.getLogMessageForUpdateSettingsBody(body);
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (assetId) {
        try {
            await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
            const isConnected = await cmService.waitUntilConnected({isHolder});

            if (!isConnected) {
                appErrorService.showDeviceSettingsError(isHolder);
                dispatch(setYapUpdateSettingsInProgress(false, isHolder));
                return;
            }

            log.info(`Start updateUamSettings process. isHolder: ${isHolder}. ${logBody}`);

            const {requestId} =
                (await new UamClient().callPost({
                    methodName: uamMethods.UPDATE_SETTINGS,
                    requestConfig: {args: assetId, data: body},
                    isNeedRedirectForOtherErrors: false,
                })) || {};

            if (requestId) {
                dispatch(setYapLastRequestId(requestId, isHolder));

                dispatch(
                    setYapRequestActionTypeData({
                        isHolder,
                        actionType: yapRunCommandActionTypes.UPDATE_SETTINGS,
                        newValue: body,
                    })
                );

                yapFlowTimeoutService.createFlowTimeout({
                    type: yapFlowTypes.UPDATE_SETTINGS,
                    timeoutInSec: uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT,
                    isHolder,
                });
                return;
            }
        } catch (e) {
            log.error(
                `uamClientService: updateUamSettings failed. isHolder: ${isHolder} error: ${e}`,
                logMessageTypes.UAM
            );
        }
    }

    appErrorService.showDeviceSettingsGlobalError();
    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const activateDevice = async (deviceSerialNumber) => {
    await tryActivateDeactivateDevice(deviceSerialNumber, true);
};

const deactivateDevice = async (deviceSerialNumber) => {
    await tryActivateDeactivateDevice(deviceSerialNumber, false);
};

const tryActivateDeactivateDevice = async (deviceSerialNumber, isActivate) => {
    const state = getState();
    const isYapActivationInProgress = makeSelectIsYapActivationInProgress()(state);

    if (isYapActivationInProgress) return;

    const actionType = isActivate ? yapRunCommandActionTypes.ACTIVATE : yapRunCommandActionTypes.DEACTIVATE;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.ACTIVATION, false);
    dispatch(setYapActivationInProgress(true));
    dispatch(setYapRequestActionTypeData({actionType}));

    try {
        const assetId = makeSelectAsset(deviceSerialNumber)(state)?.assetId;

        if (assetId) {
            try {
                await yapConnectionService.syncAndSubscribeOnDevice();
                return await activateDeactivateDevice(assetId, isActivate);
            } catch (e) {
                log.error(`uamClientService: IsActivate: ${isActivate} failed. error: ${e}`, {
                    logMessageType: logMessageTypes.UAM,
                });
            }
        }

        await iccService.fetchUamToken();
    } catch (e) {
        log.error(`uamClientService: IsActivate: ${isActivate} failed. error: ${e}`, {
            logMessageType: logMessageTypes.UAM,
        });
    }

    dispatch(setYapActivationInProgress(false));
};

const activateDeactivateDevice = async (assetId, isActivate) => {
    const isConnected = await cmService.waitUntilConnected();

    if (isConnected) {
        if (isActivate) {
            log.info('Start activation process.');
        } else {
            log.info('Start deactivation process.');
        }

        const method = isActivate ? uamMethods.ACTIVATE_DEVICE : uamMethods.DEACTIVATE_DEVICE;
        const {requestId} =
            (await new UamClient()
                .callPost({
                    methodName: method,
                    requestConfig: {args: assetId},
                    isNeedRedirectForOtherErrors: false,
                })
                .catch(() => {
                    log.error(`uamClientService: IsActivate: ${isActivate} failed. Action error`, {
                        logMessageType: logMessageTypes.UAM,
                    });
                    dispatch(setYapActivationInProgress(false));
                })) || {};

        if (requestId) {
            dispatch(setYapLastRequestId(requestId));

            yapFlowTimeoutService.createFlowTimeout({
                type: yapFlowTypes.ACTIVATION,
                timeoutInSec: uamTimeouts.UAM_ACTIVATION_TIMEOUT,
                isHolder: false,
            });
        } else {
            analyticsService.pushActivationFailedError(ACTIVATION_ERRORS_DATA.ERROR_IOT_SYSTEM_ERROR);
            dispatch(setYapActivationInProgress(false));
        }
    } else {
        dispatch(setYapActivationInProgress(false));
    }
};

const connectDevice = (assetId) =>
    new UamClient().callGet({
        methodName: uamMethods.CONNECT_DEVICE,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

const assetSubscribe = (assetId) =>
    new UamClient().callGet({
        methodName: uamMethods.ASSET_SUBSCRIBE,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

const getActivationStatus = (codentify) =>
    new UamClient().callGet({
        methodName: uamMethods.ASSET_ACTIVATION_STATUS,
        requestConfig: {args: codentify},
        ignoreErrorCodes: true,
    });

const getUamDevicesExperienceData = async ({assetId, data, headerData, codentify}) => {
    if (!data || !assetId) {
        return;
    }

    const {args, params} = data || {};
    const requestConfig = {
        args: [assetId, ...args],
        params,
    };

    if (headerData) {
        const {authToken, apiKey} = headerData || {};

        requestConfig.headers = {id_token: authToken, 'X-API-Key': apiKey};
    }

    try {
        return await new UamClient().callGet({
            methodName: uamMethods.GET_DEVICES_EXPERIENCE_DATA,
            requestConfig,
            isNeedRedirectForOtherErrors: false,
            codentify,
        });
    } catch (e) {
        log.error(`getUamDevicesExperienceData  error${e}`);
    }
};

const checkUamRequestStatus = async ({assetId, requestId}) => {
    const data = await new UamClient().callGet({
        methodName: uamMethods.CHECK_REQUEST_STATUS,
        requestConfig: {args: [assetId, requestId]},
        isNeedRedirectForOtherErrors: false,
    });

    const isFailed = data?.status === yapActionStatusTypes.FAILED;
    const isSuccess = data?.status === yapActionStatusTypes.SUCCESS;
    const isCanceled = data?.status === yapActionStatusTypes.CANCELED;

    if (isSuccess || isFailed || isCanceled) {
        return data;
    }

    return {status: yapActionStatusTypes.PENDING};
};

const findMyDevice = async (data) => {
    if (data.action === findDeviceActionTypes.STOP) {
        log.info(`uamClientService: Ending findDevice process`);
    }

    return await yapClientService.runYapCommand({
        methodName: uamMethods.FIND_MY_DEVICE,
        actionType: yapRunCommandActionTypes.FIND_DEVICE,
        params: data,
        isNeedCancel: true,
    });
};

const resetMyDevice = async (isHolder) => {
    log.info(`uamClientService: Starting resetDevice process. isHolder: ${isHolder}`);

    return await yapClientService.runYapCommand({
        isHolder,
        methodName: uamMethods.RESET_DEVICE,
        actionType: yapRunCommandActionTypes.RESET_DEVICE,
    });
};

const autoClean = async () => {
    return await yapClientService.runYapCommand({
        methodName: uamMethods.AUTO_CLEAN,
        actionType: yapRunCommandActionTypes.CLEAN_DEVICE,
        customTimeout: uamTimeouts.UAM_AUTO_CLEAN_TIMEOUT,
    });
};

const SYNCHRONIZE_DATA_RETRY_COUNT = 3;
let synchronizeDataRequestCurrentTries = 0;
const proceedSynchronizeAllData = async (isHolder, isRetry = false, dataCollectionType) => {
    if (isRetry) {
        synchronizeDataRequestCurrentTries++;
    }

    const isGlobalRetryExceeded = synchronizeDataRequestCurrentTries >= SYNCHRONIZE_DATA_RETRY_COUNT;
    const isDiagnosticRetryExceeded =
        synchronizeDataRequestCurrentTries >= 1 &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    if (isGlobalRetryExceeded || isDiagnosticRetryExceeded) {
        // set device data collection to success to run holder data collection flow or stop holder data collection
        dataCollectionService.setDataCollectionFlowReceived({isReceived: true, isHolder, dataCollectionType});

        log.error(`SynchronizeAllData failed. Retry exceeded. isHolder: ${isHolder}`, {
            logMessageType: logMessageTypes.UAM,
        });
        return resetSynchronizeDataCollection(dataCollectionType, isHolder);
    }

    const state = getState();
    const isDataCollectionReceived = dataCollectionService.getIsDataCollectionReceived(dataCollectionType, isHolder);

    if (isDataCollectionReceived) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    const isYapSynchronizeDataCollectionInProgress = makeSelectIsYapSynchronizeDataCollectionInProgress()(state);
    const isYapUsageFlowDataCollectionInProgress = makeSelectIsYapUsageFlowDataCollectionInProgress()(state);
    const isDiagnosticFlowDataCollectionInProgress = selectIsDiagnosticFlowDataCollectionInProgress(state);

    if (
        (isYapSynchronizeDataCollectionInProgress ||
            isYapUsageFlowDataCollectionInProgress ||
            isDiagnosticFlowDataCollectionInProgress) &&
        !isRetry
    )
        return;

    const isHolderConnected = selectIsHolderConnected(state);

    if (!isHolderConnected && isHolder) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    await synchronizeAllData(isHolder, dataCollectionType);
};

const getDataCollectionUserAgent = () => {
    const appId = appConfig.getIccApiHeader_a();
    const appVersion = appConfig.getAppVersion();
    const userAgent = userBrowserService.getUserAgent();

    return `${appId}/${appVersion} ${userAgent}`;
};

const resetSynchronizeDataCollection = (dataCollectionType, isHolder) => {
    synchronizeDataRequestCurrentTries = 0;

    if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
        advanceDiagnosticService.setDiagnosticFailed(isHolder);
        return;
    }

    dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
};

const synchronizeAllData = async (isHolder, dataCollectionType) => {
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    dataCollectionService.setDataCollectionFlowInProgress(true, dataCollectionType, isHolder);

    await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
    const isConnected = await cmService.waitUntilConnected({isHolder});

    if (!isConnected) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    //use getState to check last actual state
    const isYapDataCollectionAllowed = makeSelectIsYapDataCollectionAllowed()(getState());

    if (!isYapDataCollectionAllowed) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    log.info(`SynchronizeAllData is started for ${dataCollectionType} isHolder: ${isHolder}`);

    if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
        amplitudeADTracking.trackADAutoDiagnosticStarted(isHolder);
    }

    const body = {overrideEntitiesToCollect: dataCollectionService.getEntitiesToCollect(dataCollectionType)};

    const onFail = async () => {
        await helpers.timeout(1000);
        return await proceedSynchronizeAllData(isHolder, true, dataCollectionType);
    };

    const userAgent = getDataCollectionUserAgent();
    // eslint-disable-next-line no-unused-vars
    const headers = {...(await new UamClient().getHeaders(isHolder)), 'User-Agent': userAgent};

    await new UamClient()
        .callPost({
            methodName: uamMethods.SYNCHRONIZE_ALL_DATA,
            requestConfig: {args: assetId, data: body},
            isNeedRedirectForOtherErrors: false,
        })
        .then((response) => {
            const {requestId} = response || {};

            if (requestId) {
                dispatch(setDataCollectionLastRequestId(requestId, isHolder));

                advanceDiagnosticService.setCurrentCodentify(dataCollectionType, isHolder);

                yapFlowTimeoutService.createFlowTimeout({
                    type: yapFlowTypes.DATA_COLLECTION,
                    timeoutInSec: uamTimeouts.UAM_DATA_COLLECTION_TIMEOUT,
                    isHolder,
                    data: dataCollectionType,
                });
                return;
            }

            onFail();
        });
};

const CANCEL_RETRY_COUNT = 3;
let cancelCurrentTries = 0;

const cancelSynchronizeData = async (props) => {
    const {isRetry, dataCollectionType} = props || {};
    const state = getState();
    const isYapCancelDataCollectionInProgress = makeSelectYapCancelDataCollectionInProgress()(state);

    if (isYapCancelDataCollectionInProgress && !isRetry) return;
    dispatch(setYapCancelDataCollectionInProgress(true));

    const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

    if (!isDataCollectionInProgress) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    log.info(`cancelSynchronizeData is started for ${dataCollectionType} isRetry: ${isRetry}`);

    const afterCancel = (isResetProgress = false) => {
        const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

        if (isDataCollectionInProgress && isResetProgress) {
            dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
            dispatch(setDataCollectionLastRequestId(null, isHolder));
        }

        dispatch(setYapCancelDataCollectionInProgress(false));
        cancelCurrentTries = 0;
    };

    const onFail = async (error, isResetProgress = false) => {
        if (cancelCurrentTries < CANCEL_RETRY_COUNT) {
            cancelCurrentTries++;
            await helpers.timeout(1000);
            await cancelSynchronizeData({isRetry: true, dataCollectionType});
        } else {
            log.info(`cancelSynchronizeData failed. error: ${error}`, {logMessageType: logMessageTypes.UAM});
            afterCancel(isResetProgress);
        }
    };

    const deviceRequestId = makeSelectDataCollectionLastRequestId()(state);
    const holderRequestId = makeSelectDataCollectionLastRequestId(true)(state);
    const isHolder = !!holderRequestId;
    const previousRequestId = isHolder ? holderRequestId : deviceRequestId;

    if (!previousRequestId) {
        await onFail('Request id is not defined to cancel data collection', true);
        return;
    }

    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (!assetId) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    try {
        const response = await new UamClient().callGet({
            methodName: uamMethods.CANCEL_REQUEST,
            requestConfig: {args: [assetId, previousRequestId]},
            isNeedRedirectForOtherErrors: false,
        });

        const {code} = response || {};
        const CANCEL_SUCCESS_CODE = 'REQUEST_CANCELED';
        const REQUEST_ALREADY_FINISHED = 'REQUEST_ALREADY_FINISHED';
        const REQUEST_CANCELATION_SCHEDULED = 'REQUEST_CANCELATION_SCHEDULED';

        if (code === CANCEL_SUCCESS_CODE || code === REQUEST_ALREADY_FINISHED) {
            if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL) {
                amplitudeDataCollectionTracking.trackGlobalDataCollectionError(isHolder);
            }
            afterCancel(true);
            return true;
        } else if (code === REQUEST_CANCELATION_SCHEDULED) {
            cancelCurrentTries = 0;
            return true;
        }
        await onFail('Could not cancel synchronize all data');
    } catch (e) {
        await onFail(e);
    }
};

const getAssetsByToken = async ({uamAuthToken, uamApiKey}) => {
    if (!uamAuthToken || !uamApiKey) return null;

    return await new UamClient().callGet({
        methodName: uamMethods.GET_ASSETS,
        requestConfig: {
            headers: {id_token: uamAuthToken, 'X-API-Key': uamApiKey},
        },
        isNeedRedirectForOtherErrors: false,
    });
};

const getInferenceInfo = async (assetId, inferenceName) => {
    if (!inferenceName) return null;

    return await new UamClient().callGet({
        methodName: uamMethods.GET_INFERENCE_INFO,
        requestConfig: {
            args: [assetId, inferenceName],
        },
        isNeedRedirectForOtherErrors: false,
    });
};

export default {
    activateDevice,
    assetSubscribe,
    autoClean,
    cancelSynchronizeData,
    checkUamRequestStatus,
    connectDevice,
    deactivateDevice,
    findMyDevice,
    getActivationStatus,
    getUamTokenAndAssets,
    getAssets,
    getUamInfo,
    proceedSynchronizeAllData,
    resetMyDevice,
    updateUamSettings,
    getUamDevicesExperienceData,
    getAssetsByToken,
    getInferenceInfo,
};
